import { createFileRoute } from '@tanstack/react-router'
import { Code, GitPullRequest, TestTube, Clock, AlertTriangle, Filter, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { IssueDetail } from '@/components/pm/issue/issue-detail'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { type IssuePriority, type IssueStatus ,
  useMyStagesTasks,
  formatWaitingTime,
  isLongWaiting,
  type MyStageTasksFilters,
  type IssueWithWaitingTime,
} from '@/hooks'
import { useProjects } from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/pm/my-stage-tasks')({
  component: MyStageTasksPage,
})

const STATUS_LABELS: Record<IssueStatus, string> = {
  backlog: '待办',
  todo: '计划中',
  in_progress: '进行中',
  code_review: '代码审查',
  testing: '测试中',
  done: '已完成',
  blocked: '已阻塞',
  cancelled: '已取消',
}

const STATUS_COLORS: Record<IssueStatus, string> = {
  backlog: 'bg-gray-100 text-gray-800',
  todo: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  code_review: 'bg-purple-100 text-purple-800',
  testing: 'bg-orange-100 text-orange-800',
  done: 'bg-green-100 text-green-800',
  blocked: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
}

const PRIORITY_LABELS: Record<IssuePriority, string> = {
  urgent: '紧急',
  high: '高',
  medium: '中',
  low: '低',
}

const PRIORITY_COLORS: Record<IssuePriority, string> = {
  urgent: 'text-red-600',
  high: 'text-orange-600',
  medium: 'text-yellow-600',
  low: 'text-gray-600',
}

type StageKey = 'development' | 'codeReview' | 'testing'

const STAGE_CONFIG: Record<StageKey, { label: string; icon: typeof Code; color: string; bgColor: string }> = {
  development: { label: '开发中', icon: Code, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  codeReview: { label: '待评审', icon: GitPullRequest, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  testing: { label: '待测试', icon: TestTube, color: 'text-orange-600', bgColor: 'bg-orange-50' },
}

function MyStageTasksPage() {
  const [filters, setFilters] = useState<MyStageTasksFilters>({})
  const [showFilters, setShowFilters] = useState(false)
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null)
  const [activeStage, setActiveStage] = useState<StageKey | 'all'>('all')

  const { data: stageTasksResult, isLoading, error } = useMyStagesTasks(filters)
  const { data: projects } = useProjects()

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const renderTaskCard = (task: IssueWithWaitingTime) => {
    const longWaiting = isLongWaiting(task.waitingTimeMs)
    
    return (
      <div
        key={task.id}
        onClick={() => setSelectedIssueId(task.id)}
        className={cn(
          'cursor-pointer rounded-lg border p-4 transition-colors hover:bg-muted/50',
          longWaiting && 'border-red-200 bg-red-50/50'
        )}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{task.code}</span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-medium',
                  STATUS_COLORS[task.status]
                )}
              >
                {STATUS_LABELS[task.status]}
              </span>
            </div>
            <h3 className="mt-1 font-medium">{task.title}</h3>
          </div>
          <span className={cn('text-sm font-medium', PRIORITY_COLORS[task.priority])}>
            {PRIORITY_LABELS[task.priority]}
          </span>
        </div>

        <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
          <div className={cn('flex items-center gap-1', longWaiting && 'text-red-600')}>
            {longWaiting && <AlertTriangle className="h-4 w-4" />}
            <Clock className="h-4 w-4" />
            <span>等待 {formatWaitingTime(task.waitingTimeMs)}</span>
          </div>
          {task.dueDate && (
            <div className="flex items-center gap-1">
              <span>截止: {new Date(task.dueDate).toLocaleDateString('zh-CN')}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderStageSection = (stageKey: StageKey) => {
    const config = STAGE_CONFIG[stageKey]
    const Icon = config.icon
    const stageData = stageTasksResult?.[stageKey]
    const tasks = stageData?.tasks || []
    const count = stageData?.count || 0

    if (activeStage !== 'all' && activeStage !== stageKey) {
      return null
    }

    return (
      <div key={stageKey} className="space-y-4">
        <div className={cn('flex items-center gap-2 rounded-lg p-3', config.bgColor)}>
          <Icon className={cn('h-5 w-5', config.color)} />
          <h2 className={cn('font-semibold', config.color)}>{config.label}</h2>
          <span className={cn('rounded-full px-2 py-0.5 text-sm font-medium', config.color, config.bgColor)}>
            {count}
          </span>
        </div>

        {tasks.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {tasks.map(renderTaskCard)}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
            暂无{config.label}的任务
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">我的阶段任务</h1>
        <p className="text-muted-foreground">查看我在各阶段负责的任务</p>
      </div>

      {/* 统计卡片 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <button
          onClick={() => setActiveStage('all')}
          className={cn(
            'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
            activeStage === 'all' && 'border-primary bg-primary/5'
          )}
        >
          <div className="text-sm text-muted-foreground">全部任务</div>
          <div className="mt-2 text-2xl font-bold">{stageTasksResult?.total || 0}</div>
        </button>

        {(Object.keys(STAGE_CONFIG) as StageKey[]).map((stageKey) => {
          const config = STAGE_CONFIG[stageKey]
          const Icon = config.icon
          const count = stageTasksResult?.[stageKey]?.count || 0

          return (
            <button
              key={stageKey}
              onClick={() => setActiveStage(stageKey)}
              className={cn(
                'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
                activeStage === stageKey && 'border-primary bg-primary/5'
              )}
            >
              <div className={cn('flex items-center gap-2 text-sm', config.color)}>
                <Icon className="h-4 w-4" />
                {config.label}
              </div>
              <div className="mt-2 text-2xl font-bold">{count}</div>
            </button>
          )
        })}
      </div>

      {/* 筛选 */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showFilters ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <Filter className="h-4 w-4" />
            筛选
            <ChevronDown className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')} />
          </button>
        </div>

        {showFilters && (
          <div className="rounded-lg border bg-card p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium">项目</label>
                <Select
                  value={filters.projectId || 'all'}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, projectId: value === 'all' ? undefined : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部项目" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部项目</SelectItem>
                    {projects?.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 任务列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : stageTasksResult?.total === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <div className="text-center">
            <p className="text-lg font-medium">暂无阶段任务</p>
            <p className="text-sm text-muted-foreground">当前没有分配给你的阶段任务</p>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {(Object.keys(STAGE_CONFIG) as StageKey[]).map(renderStageSection)}
        </div>
      )}

      {/* 任务详情抽屉 */}
      {selectedIssueId && (
        <IssueDetail
          issueId={selectedIssueId}
          onClose={() => setSelectedIssueId(null)}
        />
      )}
    </div>
  )
}
